import { styled } from "goober";
import type { SpaceMode } from "../../../shared/schema";
import { spaceIcon } from "../../lib/spaceIcon";
import { Icon } from "../Icon";
import {
  MIN_OPTIONS_FOR_SEARCH,
  RowButton,
  SearchableDropdown,
  SectionLabel,
} from "../SearchableDropdown";
import { Text } from "../Text";
import { NO_SPACE } from "./connectorTraffic";
import { ControlButton } from "./ControlButton";

type SpacePickerOption = { id: string; label: string; mode: SpaceMode };

const PickerTrigger = styled(ControlButton)`
  max-width: 280px;
`;

export const PickerRowCheck = (
  <span style={{ flexShrink: 0, display: "flex", marginLeft: "auto" }}>
    <Icon name="check" size={12} color="var(--text-secondary)" />
  </span>
);

type Props = {
  options: readonly SpacePickerOption[];
  hasUnattributed?: boolean;
  value: string | null;
  onChange: (id: string | null) => void;
};

export function MonitoringSpacePicker({
  options,
  hasUnattributed = false,
  value,
  onChange,
}: Props) {
  const selected =
    value != null && value !== NO_SPACE
      ? options.find((option) => option.id === value)
      : undefined;
  return (
    <SearchableDropdown
      searchable={
        options.length + (hasUnattributed ? 1 : 0) >= MIN_OPTIONS_FOR_SEARCH
      }
      searchPlaceholder="Search spaces…"
      opener={
        <PickerTrigger as="span">
          {value == null ? (
            <Icon name="layers" size={12} />
          ) : value === NO_SPACE ? (
            <Icon name="circle-dashed" size={12} />
          ) : (
            <Icon name={spaceIcon(selected?.mode ?? "personal")} size={12} />
          )}
          <Text variant="text-small-md" ellipsis>
            {value == null
              ? "All spaces"
              : value === NO_SPACE
                ? "Unattributed"
                : (selected?.label ?? "All spaces")}
          </Text>
          <Icon name="chevron-down" size={12} color="var(--text-secondary)" />
        </PickerTrigger>
      }
    >
      {(query, close) => {
        const q = query.trim().toLowerCase();
        const matches = options.filter(
          (option) => !q || option.label.toLowerCase().includes(q)
        );
        const showUnattributed =
          hasUnattributed && (!q || "unattributed".includes(q));
        const pick = (id: string | null) => {
          onChange(id);
          close();
        };
        return (
          <>
            {!q && (
              <RowButton onClick={() => pick(null)}>
                <Icon name="layers" size={16} color="var(--text-primary)" />
                <div style={{ flex: 1, minWidth: 0, textAlign: "left" }}>
                  <Text variant="text-regular" ellipsis>
                    All spaces
                  </Text>
                  <Text
                    variant="text-small"
                    color="var(--text-secondary)"
                    ellipsis
                  >
                    {options.length} {options.length === 1 ? "space" : "spaces"}
                  </Text>
                </div>
                {value == null && PickerRowCheck}
              </RowButton>
            )}
            {matches.length === 0 && !showUnattributed && q !== "" ? (
              <SectionLabel>
                <Text variant="text-small" color="var(--text-secondary)">
                  No spaces found.
                </Text>
              </SectionLabel>
            ) : (
              <>
                {matches.map((option) => (
                  <RowButton key={option.id} onClick={() => pick(option.id)}>
                    <Icon name={spaceIcon(option.mode)} size={16} />
                    <div style={{ flex: 1, minWidth: 0, textAlign: "left" }}>
                      <Text variant="text-regular" ellipsis>
                        {option.label}
                      </Text>
                    </div>
                    {option.id === value && PickerRowCheck}
                  </RowButton>
                ))}
                {showUnattributed && (
                  <RowButton onClick={() => pick(NO_SPACE)}>
                    <Icon name="circle-dashed" size={16} />
                    <div style={{ flex: 1, minWidth: 0, textAlign: "left" }}>
                      <Text variant="text-regular" ellipsis>
                        Unattributed
                      </Text>
                      <Text
                        variant="text-small"
                        color="var(--text-secondary)"
                        ellipsis
                      >
                        Usage with no space
                      </Text>
                    </div>
                    {value === NO_SPACE && PickerRowCheck}
                  </RowButton>
                )}
              </>
            )}
          </>
        );
      }}
    </SearchableDropdown>
  );
}
